import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { 
  Users, TrendingUp, IndianRupee, Percent, Filter, X, RefreshCw, 
  AlertTriangle, MapPin, Building2, Trophy, PieChart as PieChartIcon, Search,
  CheckCircle2, AlertCircle
} from "lucide-react";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend
} from "recharts";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import { filterQuotations, computeSellerStats } from "@/lib/sellerRanking";
import { useNavigate } from "react-router-dom";

const SELLER_COLORS = ["#14b8a6", "#f97316", "#8b5cf6", "#3b82f6", "#ec4899", "#10b981", "#94a3b8", "#eab308", "#ef4444", "#06b6d4"];
const ALL_BRANDS = ["Wellcore", "Dynamite", "Okami", "YouWeFit", "Zero Sugar", "OSMO"];
const HIGH_DISCOUNT_THRESHOLD = 62; // Alert threshold for effective discount %

export default function SellerInsights() {
  const navigate = useNavigate();
  const [quotations, setQuotations] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contributionLimit, setContributionLimit] = useState(25);

  // Seller Revenue Contribution "View All" modal
  const [contributionModalOpen, setContributionModalOpen] = useState(false);
  const [contributionSearch, setContributionSearch] = useState("");
  
  // Default to current month
  const now = new Date();
  const monthStart = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-01`;
  const today = now.toISOString().substring(0,10);
  
  // Filter states
  const [dateFrom, setDateFrom] = useState(monthStart);
  const [dateTo, setDateTo] = useState(today);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedSkus, setSelectedSkus] = useState([]);
  const [selectedSellers, setSelectedSellers] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedStates, setSelectedStates] = useState([]);
  
  // Popover states
  const [brandFilterOpen, setBrandFilterOpen] = useState(false);
  const [skuFilterOpen, setSkuFilterOpen] = useState(false);
  const [sellerFilterOpen, setSellerFilterOpen] = useState(false);
  const [cityFilterOpen, setCityFilterOpen] = useState(false);
  const [stateFilterOpen, setStateFilterOpen] = useState(false);

  // Per-dropdown search text (client-side, case-insensitive, partial match)
  const [brandSearch, setBrandSearch] = useState("");
  const [skuSearch, setSkuSearch] = useState("");
  const [sellerSearch, setSellerSearch] = useState("");
  const [citySearch, setCitySearch] = useState("");
  const [stateSearch, setStateSearch] = useState("");

  // "View All" navigates to the dedicated Seller Revenue Ranking page, passing current filters.
  const openRanking = () => {
    const params = new URLSearchParams();
    if (dateFrom) params.set("dateFrom", dateFrom);
    if (dateTo) params.set("dateTo", dateTo);
    if (selectedBrands.length) params.set("brands", selectedBrands.join(","));
    if (selectedSkus.length) params.set("skus", selectedSkus.join(","));
    if (selectedSellers.length) params.set("sellers", selectedSellers.join(","));
    if (selectedCities.length) params.set("cities", selectedCities.join(","));
    if (selectedStates.length) params.set("states", selectedStates.join(","));
    navigate(`/seller-revenue-ranking?${params.toString()}`);
  };

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [quotationsRes, sellersRes, productsRes] = await Promise.all([
        axios.get(`${API}/quotations`, { withCredentials: true }),
        axios.get(`${API}/sellers`, { withCredentials: true }),
        axios.get(`${API}/products`, { withCredentials: true })
      ]);
      setQuotations(quotationsRes.data || []);
      setSellers(sellersRes.data || []);
      setProducts(productsRes.data || []);
      try {
        const limitRes = await axios.get(`${API}/settings/seller-contribution-limit`, { withCredentials: true });
        if (limitRes.data?.limit != null) setContributionLimit(Number(limitRes.data.limit));
      } catch (_) { /* keep default */ }
    } catch (error) {
      // Error fetching data
    } finally {
      setLoading(false);
    }
  };

  // Get unique cities and states from sellers
  const { uniqueCities, uniqueStates } = useMemo(() => {
    const cities = new Set();
    const states = new Set();
    sellers.forEach(s => {
      if (s.city) cities.add(s.city);
      if (s.state) states.add(s.state);
    });
    return {
      uniqueCities: Array.from(cities).sort(),
      uniqueStates: Array.from(states).sort()
    };
  }, [sellers]);

  // Get unique SKUs
  const uniqueSkus = useMemo(() => {
    const skuSet = new Map();
    products.forEach(p => {
      if (p.sku_id) {
        skuSet.set(p.sku_id, { sku_id: p.sku_id, product_name: p.product_name, brand: p.brand });
      }
    });
    return Array.from(skuSet.values()).sort((a, b) => a.product_name?.localeCompare(b.product_name));
  }, [products]);

  // Create seller lookup map with city/state
  const sellerMap = useMemo(() => {
    const map = {};
    sellers.forEach(s => {
      map[s.id] = s;
    });
    return map;
  }, [sellers]);

  // Filter quotations based on all filters (shared logic — see lib/sellerRanking).
  const filteredQuotations = useMemo(
    () => filterQuotations(quotations, sellerMap, { dateFrom, dateTo, selectedSellers, selectedCities, selectedStates, selectedBrands, selectedSkus }),
    [quotations, dateFrom, dateTo, selectedSellers, selectedCities, selectedStates, selectedBrands, selectedSkus, sellerMap]
  );

  // Calculate seller statistics (grouped by seller_name to merge duplicates)
  const sellerStats = useMemo(
    () => computeSellerStats(filteredQuotations, sellerMap, products, { selectedBrands, selectedSkus }),
    [filteredQuotations, sellerMap, products, selectedBrands, selectedSkus]
  );

  // Top 5 sellers for chart
  const top5Sellers = useMemo(() => sellerStats.slice(0, 5), [sellerStats]);

  // Top 10 sellers for table
  const top10Sellers = useMemo(() => sellerStats.slice(0, 10), [sellerStats]);

  // High discount sellers (above threshold)
  const highDiscountSellers = useMemo(() => {
    return sellerStats.filter(s => s.avgEffectiveDiscount > HIGH_DISCOUNT_THRESHOLD && s.quotationCount > 0);
  }, [sellerStats]);

  // Seller contribution data for pie chart
  const sellerContributionData = useMemo(() => {
    const total = top5Sellers.reduce((sum, s) => sum + s.totalRevenue, 0);
    const othersRevenue = sellerStats.slice(5).reduce((sum, s) => sum + s.totalRevenue, 0);
    
    const data = top5Sellers.map(s => ({
      name: s.name.length > 12 ? s.name.substring(0, 12) + "..." : s.name,
      fullName: s.name,
      value: s.totalRevenue,
      percentage: (total + othersRevenue) > 0 ? ((s.totalRevenue / (total + othersRevenue)) * 100).toFixed(1) : 0
    }));
    
    if (othersRevenue > 0) {
      data.push({
        name: "Others",
        fullName: "Others",
        value: othersRevenue,
        percentage: ((othersRevenue / (total + othersRevenue)) * 100).toFixed(1)
      });
    }
    
    return data;
  }, [top5Sellers, sellerStats]);

  // Full seller-wise revenue contribution list (for the "View All" modal).
  // Contribution % = seller's share of total revenue across all sellers in range.
  const sellerContributionList = useMemo(() => {
    const totalRevenue = sellerStats.reduce((sum, s) => sum + s.totalRevenue, 0);
    return sellerStats
      .filter(s => s.totalRevenue > 0)
      .map(s => {
        const pct = totalRevenue > 0 ? (s.totalRevenue / totalRevenue) * 100 : 0;
        return {
          id: s.id,
          name: s.name,
          city: s.city,
          state: s.state,
          revenue: s.totalRevenue,
          contributionPct: pct,
          exceedsLimit: pct > contributionLimit,
        };
      })
      .sort((a, b) => b.revenue - a.revenue);
  }, [sellerStats, contributionLimit]);

  const filteredContributionList = useMemo(() => {
    const q = contributionSearch.trim().toLowerCase();
    if (!q) return sellerContributionList;
    return sellerContributionList.filter(
      s => (s.name || "").toLowerCase().includes(q) || (s.city || "").toLowerCase().includes(q) || (s.state || "").toLowerCase().includes(q)
    );
  }, [sellerContributionList, contributionSearch]);

  const contributionExceedCount = useMemo(
    () => sellerContributionList.filter(s => s.exceedsLimit).length,
    [sellerContributionList]
  );

  // City-wise analysis
  const cityAnalysis = useMemo(() => {
    const cityStats = {};
    
    filteredQuotations.forEach(q => {
      const seller = sellerMap[q.seller_id];
      const city = seller?.city || "Unknown";
      
      if (!cityStats[city]) {
        cityStats[city] = { city, revenue: 0, discounts: [] };
      }
      
      let items = q.items || [];
      if (selectedBrands.length > 0) {
        items = items.filter(item => selectedBrands.includes(item.brand));
      }
      if (selectedSkus.length > 0) {
        items = items.filter(item => selectedSkus.includes(item.sku_id));
      }
      
      const revenue = selectedBrands.length > 0 || selectedSkus.length > 0
        ? items.reduce((sum, item) => sum + (item.invoice_value || 0), 0)
        : (q.total_invoice_value || 0);
      
      cityStats[city].revenue += revenue;
      if (q.effective_discount != null) {
        cityStats[city].discounts.push(q.effective_discount);
      }
    });
    
    return Object.values(cityStats)
      .map(c => ({
        ...c,
        avgDiscount: c.discounts.length > 0 ? c.discounts.reduce((a, b) => a + b, 0) / c.discounts.length : 0
      }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10);
  }, [filteredQuotations, sellerMap, selectedBrands, selectedSkus]);

  // State-wise analysis
  const stateAnalysis = useMemo(() => {
    const stateStats = {};
    
    filteredQuotations.forEach(q => {
      const seller = sellerMap[q.seller_id];
      const state = seller?.state || "Unknown";
      
      if (!stateStats[state]) {
        stateStats[state] = { state, revenue: 0, discounts: [] };
      }
      
      let items = q.items || [];
      if (selectedBrands.length > 0) {
        items = items.filter(item => selectedBrands.includes(item.brand));
      }
      if (selectedSkus.length > 0) {
        items = items.filter(item => selectedSkus.includes(item.sku_id));
      }
      
      const revenue = selectedBrands.length > 0 || selectedSkus.length > 0
        ? items.reduce((sum, item) => sum + (item.invoice_value || 0), 0)
        : (q.total_invoice_value || 0);
      
      stateStats[state].revenue += revenue;
      if (q.effective_discount != null) {
        stateStats[state].discounts.push(q.effective_discount);
      }
    });
    
    return Object.values(stateStats)
      .map(s => ({
        ...s,
        avgDiscount: s.discounts.length > 0 ? s.discounts.reduce((a, b) => a + b, 0) / s.discounts.length : 0
      }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10);
  }, [filteredQuotations, sellerMap, selectedBrands, selectedSkus]);

  // Format functions
  const formatCurrency = (value) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
    if (value >= 1000) return `₹${(value / 1000).toFixed(1)} K`;
    return `₹${value.toLocaleString('en-IN')}`;
  };

  // Toggle filter functions
  const toggleBrandFilter = (brand) => {
    setSelectedBrands(prev => prev.includes(brand) ? prev.filter(b => b !== brand) : [...prev, brand]);
  };
  const toggleSkuFilter = (skuId) => {
    setSelectedSkus(prev => prev.includes(skuId) ? prev.filter(s => s !== skuId) : [...prev, skuId]);
  };
  const toggleSellerFilter = (sellerId) => {
    setSelectedSellers(prev => prev.includes(sellerId) ? prev.filter(s => s !== sellerId) : [...prev, sellerId]);
  };
  const toggleCityFilter = (city) => {
    setSelectedCities(prev => prev.includes(city) ? prev.filter(c => c !== city) : [...prev, city]);
  };
  const toggleStateFilter = (state) => {
    setSelectedStates(prev => prev.includes(state) ? prev.filter(s => s !== state) : [...prev, state]);
  };

  const clearFilters = () => {
    setDateFrom(monthStart);
    setDateTo(today);
    setSelectedBrands([]);
    setSelectedSkus([]);
    setSelectedSellers([]);
    setSelectedCities([]);
    setSelectedStates([]);
  };

  const hasActiveFilters = dateFrom || dateTo || selectedBrands.length > 0 || selectedSkus.length > 0 || 
    selectedSellers.length > 0 || selectedCities.length > 0 || selectedStates.length > 0;

  // Custom tooltips
  const CustomBarTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 rounded-lg border shadow-lg">
          <p className="font-semibold text-sm">{data.fullName || data.name || data.city || data.state}</p>
          <p className="text-sm text-muted-foreground mt-1">
            Revenue: <span className="font-mono font-medium text-slate-900">{formatCurrency(data.revenue || data.value)}</span>
          </p>
          {data.avgDiscount !== undefined && (
            <p className="text-sm text-muted-foreground">
              Avg Discount: <span className="font-mono font-medium text-slate-900">{data.avgDiscount.toFixed(1)}%</span>
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  // Checkbox component for filters
  const FilterCheckbox = ({ checked, label, sublabel, onClick }) => (
    <div
      className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-100 ${checked ? 'bg-accent/10' : ''}`}
      onClick={onClick}
    >
      <div className={`w-4 h-4 rounded border flex-shrink-0 ${checked ? 'bg-accent border-accent' : 'border-slate-300'}`}>
        {checked && (
          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm truncate">{label}</p>
        {sublabel && <p className="text-xs text-muted-foreground truncate">{sublabel}</p>}
      </div>
    </div>
  );

  // Sticky search box rendered at the top of each filter dropdown. Client-side only.
  // Defined as a render-FUNCTION (not a JSX component) so the input keeps focus across keystrokes.
  const renderSearch = (value, onChange, placeholder, testId) => (
    <div className="sticky top-0 z-10 bg-white pb-2 mb-1 border-b border-slate-100">
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
        <Input
          autoFocus
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="h-8 pl-7 text-xs"
          data-testid={testId}
        />
      </div>
    </div>
  );

  const includesCI = (text, query) => (text || "").toLowerCase().includes(query.trim().toLowerCase());

  // Client-side filtered option lists (case-insensitive, partial match).
  const filteredBrands = ALL_BRANDS.filter(b => includesCI(b, brandSearch));
  const filteredSkus = uniqueSkus.filter(s => includesCI(s.product_name, skuSearch) || includesCI(s.sku_id, skuSearch));
  const filteredSellers = sellers.filter(s => includesCI(s.seller_name, sellerSearch) || includesCI(s.city, sellerSearch));
  const filteredCities = uniqueCities.filter(c => includesCI(c, citySearch));
  const filteredStates = uniqueStates.filter(s => includesCI(s, stateSearch));


  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-xl font-semibold text-[#0F0F0F]" data-testid="seller-insights-title">Seller Insights</h1>
          <p className="text-muted-foreground mt-1">Analyze seller performance and geography trends</p>
        </div>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Sticky Header + Inline Filters */}
      <div className="bg-white pb-4 mb-1 border-b border-[#E8E8E8]">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-xl font-semibold text-[#0F0F0F] shrink-0 mr-auto" data-testid="seller-insights-title">Seller Insights</h1>

          <DateRangeFilter from={dateFrom} to={dateTo} onChange={(f,t)=>{ setDateFrom(f); setDateTo(t); }} testId="seller-date-range" />

          <Popover open={brandFilterOpen} onOpenChange={setBrandFilterOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-8 text-xs px-3 border-[#E8E8E8]">
                {selectedBrands.length === 0 ? "Brands" : `${selectedBrands.length} Brand${selectedBrands.length > 1 ? "s" : ""}`}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-2">
              {renderSearch(brandSearch, setBrandSearch, "Search Brands...", "seller-insights-brand-search")}
              <div className="max-h-64 overflow-y-auto space-y-1">
                {filteredBrands.map(brand => (
                  <FilterCheckbox key={brand} checked={selectedBrands.includes(brand)} label={brand} onClick={() => toggleBrandFilter(brand)} />
                ))}
                {filteredBrands.length === 0 && <p className="text-xs text-muted-foreground text-center py-3">No brands found</p>}
              </div>
            </PopoverContent>
          </Popover>

          <Popover open={skuFilterOpen} onOpenChange={setSkuFilterOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-8 text-xs px-3 border-[#E8E8E8]">
                {selectedSkus.length === 0 ? "SKUs" : `${selectedSkus.length} SKU${selectedSkus.length > 1 ? "s" : ""}`}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-2">
              {renderSearch(skuSearch, setSkuSearch, "Search SKUs...", "seller-insights-sku-search")}
              <div className="max-h-64 overflow-y-auto space-y-1">
                {filteredSkus.slice(0, 50).map(sku => (
                  <FilterCheckbox key={sku.sku_id} checked={selectedSkus.includes(sku.sku_id)} label={sku.product_name} sublabel={sku.sku_id} onClick={() => toggleSkuFilter(sku.sku_id)} />
                ))}
                {filteredSkus.length === 0 && <p className="text-xs text-muted-foreground text-center py-3">No SKUs found</p>}
                {filteredSkus.length > 50 && <p className="text-xs text-muted-foreground text-center py-2">Showing first 50</p>}
              </div>
            </PopoverContent>
          </Popover>

          <Popover open={sellerFilterOpen} onOpenChange={setSellerFilterOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-8 text-xs px-3 border-[#E8E8E8]">
                {selectedSellers.length === 0 ? "Sellers" : `${selectedSellers.length} Seller${selectedSellers.length > 1 ? "s" : ""}`}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-2">
              {renderSearch(sellerSearch, setSellerSearch, "Search Sellers...", "seller-insights-seller-search")}
              <div className="max-h-64 overflow-y-auto space-y-1">
                {filteredSellers.slice(0, 50).map(seller => (
                  <FilterCheckbox key={seller.id} checked={selectedSellers.includes(seller.id)} label={seller.seller_name} sublabel={seller.city} onClick={() => toggleSellerFilter(seller.id)} />
                ))}
                {filteredSellers.length === 0 && <p className="text-xs text-muted-foreground text-center py-3">No sellers found</p>}
                {filteredSellers.length > 50 && <p className="text-xs text-muted-foreground text-center py-2">Showing first 50</p>}
              </div>
            </PopoverContent>
          </Popover>

          <Popover open={cityFilterOpen} onOpenChange={setCityFilterOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-8 text-xs px-3 border-[#E8E8E8]">
                {selectedCities.length === 0 ? "Cities" : `${selectedCities.length} Cit${selectedCities.length > 1 ? "ies" : "y"}`}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-2">
              {renderSearch(citySearch, setCitySearch, "Search Cities...", "seller-insights-city-search")}
              <div className="max-h-64 overflow-y-auto space-y-1">
                {filteredCities.map(city => (
                  <FilterCheckbox key={city} checked={selectedCities.includes(city)} label={city} onClick={() => toggleCityFilter(city)} />
                ))}
                {filteredCities.length === 0 && <p className="text-xs text-muted-foreground text-center py-3">No cities found</p>}
              </div>
            </PopoverContent>
          </Popover>

          <Popover open={stateFilterOpen} onOpenChange={setStateFilterOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-8 text-xs px-3 border-[#E8E8E8]">
                {selectedStates.length === 0 ? "States" : `${selectedStates.length} State${selectedStates.length > 1 ? "s" : ""}`}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-2">
              {renderSearch(stateSearch, setStateSearch, "Search States...", "seller-insights-state-search")}
              <div className="max-h-64 overflow-y-auto space-y-1">
                {filteredStates.map(state => (
                  <FilterCheckbox key={state} checked={selectedStates.includes(state)} label={state} onClick={() => toggleStateFilter(state)} />
                ))}
                {filteredStates.length === 0 && <p className="text-xs text-muted-foreground text-center py-3">No states found</p>}
              </div>
            </PopoverContent>
          </Popover>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 px-2 text-xs text-red-500 hover:text-red-600 hover:bg-red-50">
              <X className="h-3 w-3 mr-1" />Clear
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={fetchData} className="h-8 px-2 border-[#E8E8E8]">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {dateFrom && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#E8E8E8]/50 rounded text-[11px]">From: {dateFrom} <X className="h-2.5 w-2.5 cursor-pointer" onClick={() => setDateFrom("")} /></span>}
            {dateTo && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#E8E8E8]/50 rounded text-[11px]">To: {dateTo} <X className="h-2.5 w-2.5 cursor-pointer" onClick={() => setDateTo("")} /></span>}
            {selectedBrands.map(b => <span key={b} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#E8E8E8]/50 rounded text-[11px]">{b} <X className="h-2.5 w-2.5 cursor-pointer" onClick={() => toggleBrandFilter(b)} /></span>)}
            {selectedSellers.map(id => <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#E8E8E8]/50 rounded text-[11px]">{sellerMap[id]?.seller_name || id} <X className="h-2.5 w-2.5 cursor-pointer" onClick={() => toggleSellerFilter(id)} /></span>)}
            {selectedCities.map(c => <span key={c} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#E8E8E8]/50 rounded text-[11px]">{c} <X className="h-2.5 w-2.5 cursor-pointer" onClick={() => toggleCityFilter(c)} /></span>)}
            {selectedStates.map(s => <span key={s} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#E8E8E8]/50 rounded text-[11px]">{s} <X className="h-2.5 w-2.5 cursor-pointer" onClick={() => toggleStateFilter(s)} /></span>)}
          </div>
        )}
      </div>

      {/* ============= A. TOP SELLERS BY REVENUE ============= */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold tracking-tight font-heading flex items-center gap-2">
            <Trophy className="h-6 w-6 text-amber-500" />
            Top Sellers by Revenue
          </h2>
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs border-[#E8E8E8]"
            onClick={openRanking}
            disabled={sellerStats.length === 0}
            data-testid="seller-insights-view-all"
          >
            View All
          </Button>
        </div>
        <div className="grid gap-4 md:grid-cols-5">
          {top5Sellers.length > 0 ? (
            top5Sellers.map((seller, index) => (
              <Card key={seller.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <span className={`text-2xl font-bold ${index === 0 ? 'text-amber-500' : index === 1 ? 'text-slate-400' : index === 2 ? 'text-amber-700' : 'text-slate-500'}`}>
                      #{index + 1}
                    </span>
                    <div className="p-2 rounded-lg" style={{ backgroundColor: `${SELLER_COLORS[index]}20` }}>
                      <IndianRupee className="h-4 w-4" style={{ color: SELLER_COLORS[index] }} />
                    </div>
                  </div>
                  <CardTitle className="text-sm font-medium truncate" title={seller.name}>{seller.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-xl font-bold font-mono" style={{ color: SELLER_COLORS[index] }}>
                    {formatCurrency(seller.totalRevenue)}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{seller.quotationCount} quotations</p>
                </CardContent>
              </Card>
            ))
          ) : (
            <div className="col-span-5 text-center py-8 text-muted-foreground">No seller data available</div>
          )}
        </div>
      </div>

      {/* ============= B. SELLER CONTRIBUTION ============= */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <PieChartIcon className="h-5 w-5 text-purple-500" />
                Seller Revenue Contribution
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">Share of total revenue by top sellers</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs border-[#E8E8E8] shrink-0"
              onClick={() => { setContributionSearch(""); setContributionModalOpen(true); }}
              disabled={sellerContributionList.length === 0}
              data-testid="seller-contribution-view-all"
            >
              View All
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {sellerContributionData.length > 0 ? (
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={sellerContributionData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percentage }) => `${name}: ${percentage}%`}
                    labelLine={false}
                  >
                    {sellerContributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={SELLER_COLORS[index % SELLER_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomBarTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-muted-foreground">No contribution data available</div>
          )}
        </CardContent>
      </Card>

      {/* ============= C. SELLER PERFORMANCE TABLE (TOP 10) ============= */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-500" />
            Seller Performance (Top 10)
          </CardTitle>
          <p className="text-sm text-muted-foreground">Detailed metrics for top performing sellers{hasActiveFilters && " (filtered)"}</p>
        </CardHeader>
        <CardContent>
          {top10Sellers.length > 0 ? (
            <div className="overflow-auto max-h-[400px]">
              <Table>
                <TableHeader className="sticky top-0 bg-white">
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">#</TableHead>
                    <TableHead className="font-semibold">Seller Name</TableHead>
                    <TableHead className="font-semibold">City</TableHead>
                    <TableHead className="font-semibold">State</TableHead>
                    <TableHead className="font-semibold text-right">Total Revenue</TableHead>
                    <TableHead className="font-semibold text-right">Quotations</TableHead>
                    <TableHead className="font-semibold text-right">Avg. Discount %</TableHead>
                    <TableHead className="font-semibold text-right">Avg. Freebie %</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {top10Sellers.map((seller, index) => (
                    <TableRow key={seller.id}>
                      <TableCell className="font-medium">{index + 1}</TableCell>
                      <TableCell className="max-w-[180px] truncate" title={seller.name}>{seller.name}</TableCell>
                      <TableCell>{seller.city}</TableCell>
                      <TableCell>{seller.state}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(seller.totalRevenue)}</TableCell>
                      <TableCell className="text-right">{seller.quotationCount}</TableCell>
                      <TableCell className="text-right">
                        <span className={`font-mono ${seller.avgEffectiveDiscount > HIGH_DISCOUNT_THRESHOLD ? 'text-red-600 font-semibold' : 'text-green-600'}`}>
                          {seller.avgEffectiveDiscount.toFixed(1)}%
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-mono">{seller.freebiePercent.toFixed(1)}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex items-center justify-center h-[200px] text-muted-foreground">No seller data available</div>
          )}
        </CardContent>
      </Card>

      {/* ============= D. HIGH DISCOUNT SELLERS ALERT ============= */}
      {highDiscountSellers.length > 0 && (
        <Card className="border-red-200 bg-red-50/30">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />
              High Discount Alert (Above {HIGH_DISCOUNT_THRESHOLD}%)
            </CardTitle>
            <p className="text-sm text-red-600">Sellers with average effective discount exceeding threshold</p>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {highDiscountSellers.slice(0, 6).map(seller => (
                <div key={seller.id} className="bg-white p-3 rounded-lg border border-red-200">
                  <p className="font-medium text-sm truncate" title={seller.name}>{seller.name}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-muted-foreground">{seller.city}, {seller.state}</span>
                    <span className="font-mono font-bold text-red-600">{seller.avgEffectiveDiscount.toFixed(1)}%</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Revenue: {formatCurrency(seller.totalRevenue)}</p>
                </div>
              ))}
            </div>
            {highDiscountSellers.length > 6 && (
              <p className="text-sm text-red-600 mt-3">+ {highDiscountSellers.length - 6} more sellers above threshold</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* ============= E. CITY-WISE ANALYSIS ============= */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Building2 className="h-5 w-5 text-teal-500" />
            City-wise Analysis
          </CardTitle>
          <p className="text-sm text-muted-foreground">Revenue and average discount by city (Top 10)</p>
        </CardHeader>
        <CardContent>
          {cityAnalysis.length > 0 ? (
            <div className="h-[350px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={cityAnalysis} layout="vertical" margin={{ left: 10, right: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="city" width={100} tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomBarTooltip />} />
                  <Bar dataKey="revenue" name="Revenue" radius={[0, 4, 4, 0]}>
                    {cityAnalysis.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={SELLER_COLORS[index % SELLER_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-muted-foreground">No city data available</div>
          )}
        </CardContent>
      </Card>

      {/* ============= F. STATE-WISE ANALYSIS ============= */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <MapPin className="h-5 w-5 text-orange-500" />
            State-wise Analysis
          </CardTitle>
          <p className="text-sm text-muted-foreground">Revenue and average discount by state (Top 10)</p>
        </CardHeader>
        <CardContent>
          {stateAnalysis.length > 0 ? (
            <div className="h-[350px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stateAnalysis} layout="vertical" margin={{ left: 10, right: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="state" width={120} tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomBarTooltip />} />
                  <Bar dataKey="revenue" name="Revenue" radius={[0, 4, 4, 0]}>
                    {stateAnalysis.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={SELLER_COLORS[index % SELLER_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-muted-foreground">No state data available</div>
          )}
        </CardContent>
      </Card>

      {/* ============= SELLER REVENUE CONTRIBUTION — VIEW ALL MODAL ============= */}
      <Dialog open={contributionModalOpen} onOpenChange={setContributionModalOpen}>
        <DialogContent className="max-w-3xl" data-testid="seller-contribution-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PieChartIcon className="h-5 w-5 text-purple-500" />
              Seller Revenue Contribution — All Sellers
            </DialogTitle>
            <DialogDescription>
              Each seller's share of total revenue for the selected period. Flagged when share exceeds the configured limit of {contributionLimit.toFixed(2)}%.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <Input
                value={contributionSearch}
                onChange={(e) => setContributionSearch(e.target.value)}
                placeholder="Search seller, city, state..."
                className="h-8 pl-7 text-xs"
                data-testid="seller-contribution-modal-search"
              />
            </div>
            <span className="text-xs text-muted-foreground" data-testid="seller-contribution-exceed-count">
              {contributionExceedCount} of {sellerContributionList.length} above {contributionLimit.toFixed(2)}%
            </span>
          </div>

          <div className="overflow-auto max-h-[55vh] mt-2">
            <Table>
              <TableHeader className="sticky top-0 bg-white z-10">
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">#</TableHead>
                  <TableHead className="font-semibold">Seller Name</TableHead>
                  <TableHead className="font-semibold text-right">Revenue</TableHead>
                  <TableHead className="font-semibold text-right">Contribution %</TableHead>
                  <TableHead className="font-semibold text-right">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredContributionList.length > 0 ? (
                  filteredContributionList.map((s, index) => (
                    <TableRow key={s.id} data-testid={`seller-contribution-row-${s.id}`}>
                      <TableCell className="font-medium">{index + 1}</TableCell>
                      <TableCell className="max-w-[220px]">
                        <p className="truncate" title={s.name}>{s.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{[s.city, s.state].filter(Boolean).join(", ")}</p>
                      </TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(s.revenue)}</TableCell>
                      <TableCell className="text-right font-mono font-semibold">{s.contributionPct.toFixed(2)}%</TableCell>
                      <TableCell className="text-right">
                        {s.exceedsLimit ? (
                          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300 gap-1">
                            <AlertCircle className="h-3 w-3" /> Limit Exceeded
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 gap-1">
                            <CheckCircle2 className="h-3 w-3" /> Within Limit
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      No sellers found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
